import React, { useEffect, useState } from 'react';
import * as microsoftTeams from '@microsoft/teams-js';

/**
 * AuthEnd component - Handles the authentication callback
 * This page receives the redirect from Azure AD after authentication
 */
const AuthEnd = () => {
    const [status, setStatus] = useState('Authentifizierung wird abgeschlossen...');
    const [error, setError] = useState(null);

    useEffect(() => {
        console.log("Auth End: Component mounted. URL:", window.location.href);

        const completeAuth = async () => {
            try {
                // 1. Initialize Teams SDK immediately
                console.log("Auth End: Initializing SDK...");
                setStatus('Verbindung mit Teams...');
                await microsoftTeams.app.initialize();
                console.log("Auth End: SDK Initialized");

                // 2. Robust parameter extraction
                const getParam = (name) => {
                    const searchParams = new URLSearchParams(window.location.search);
                    if (searchParams.has(name)) return searchParams.get(name);
                    const hash = window.location.hash.substring(1).replace(/^\//, '');
                    return new URLSearchParams(hash).get(name);
                };

                const urlError = getParam('error');
                const urlErrorDesc = getParam('error_description');

                // 3. Handle Errors immediately
                if (urlError) {
                    console.error("Auth End: Error detected:", urlError, urlErrorDesc);
                    setStatus('Anmeldung abgebrochen. Wird geschlossen...');
                    microsoftTeams.authentication.notifyFailure(urlErrorDesc || urlError);
                    return;
                }

                // 4. Get Teams context
                const context = await microsoftTeams.app.getContext();

                const clientId = process.env.REACT_APP_CLIENT_ID;
                if (!clientId) throw new Error("Missing Client ID");

                // 5. MSAL Interaction
                const msal = await import('@azure/msal-browser');
                const msalConfig = {
                    auth: {
                        clientId: clientId,
                        authority: `https://login.microsoftonline.com/${context.user.tenant.id || 'common'}`,
                        redirectUri: window.location.origin + "/auth-end", // Must match exactly
                        navigateToLoginRequestUrl: false
                    },
                    cache: { cacheLocation: "sessionStorage" }
                };

                const msalInstance = new msal.PublicClientApplication(msalConfig);
                await msalInstance.initialize();

                const watchdog = setTimeout(() => {
                    microsoftTeams.authentication.notifyFailure("Authentifizierung abgelaufen.");
                }, 10000);

                const tokenResponse = await msalInstance.handleRedirectPromise();
                clearTimeout(watchdog);

                if (tokenResponse) {
                    console.log("Auth End: Success!");
                    setStatus('Erfolgreich! Wird geschlossen...');
                    microsoftTeams.authentication.notifySuccess(JSON.stringify(tokenResponse));
                } else {
                    const accounts = msalInstance.getAllAccounts();
                    if (accounts.length > 0) {
                        microsoftTeams.authentication.notifySuccess(JSON.stringify({ account: accounts[0] }));
                    } else {
                        microsoftTeams.authentication.notifyFailure("Keine Antwort erhalten.");
                    }
                }
            } catch (err) {
                console.error('Auth End Error:', err);
                setError(err.message);
                setStatus('Fehlgeschlagen');
                try {
                    microsoftTeams.authentication.notifyFailure(err.message || 'Verification failed');
                } catch (e) { }
            }
        };

        completeAuth();
    }, []);

    return (
        <div style={{
            padding: '40px',
            textAlign: 'center',
            fontFamily: 'Segoe UI, sans-serif',
            backgroundColor: '#f3f2f1',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
        }}>
            {!error && <div className="loader" style={{
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #6264a7',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                animation: 'spin 2s linear infinite',
                marginBottom: '20px'
            }} />}
            <style>{`
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
            `}</style>

            <h2 style={{ color: '#242424' }}>{status}</h2>

            {error && (
                <div style={{
                    marginTop: '20px',
                    padding: '15px',
                    backgroundColor: '#fde7e9',
                    color: '#a4262c',
                    borderRadius: '4px',
                    border: '1px solid #a4262c'
                }}>
                    <strong>Fehler:</strong> {error}
                </div>
            )}
        </div>
    );
};

export default AuthEnd;
